import { useCallback, useEffect, useState } from 'react';
import { foldersApi } from '@/shared/folders';
import type { FoldersNode, FoldersNodeOpen, FoldersSharedNode } from '@/shared/folders';

export interface HomeWorkspace {
  readonly starred: readonly FoldersNode[];
  readonly shared: readonly FoldersSharedNode[];
  readonly recent: readonly FoldersNodeOpen[];
  readonly frequent: readonly FoldersNodeOpen[];
  readonly starredIds: ReadonlySet<string>;
  readonly loading: boolean;
  /** True when at least one section failed; the others still render. */
  readonly failed: boolean;
  readonly reload: () => void;
  readonly toggleStar: (node: FoldersNode) => void;
}

const RECENT_LIMIT = 8;
/** Feeds the Shared tab and lets starred shared files into Starred. */
const SHARED_LIMIT = 50;
const FREQUENT_LIMIT = 4;

/**
 * Everything the Home sections need, in one place.
 *
 * The four lists are fetched together with `allSettled` rather than
 * `Promise.all`: they are independent sections and one failing query should
 * cost the user that section, not the whole page. Frequently opened subtracts
 * recents client-side so both can go out in the same round.
 */
export function useHomeWorkspace(orgId: string | null, userId: string | null): HomeWorkspace {
  const [starred, setStarred] = useState<readonly FoldersNode[]>([]);
  const [shared, setShared] = useState<readonly FoldersSharedNode[]>([]);
  const [recent, setRecent] = useState<readonly FoldersNodeOpen[]>([]);
  const [frequent, setFrequent] = useState<readonly FoldersNodeOpen[]>([]);
  const [starredIds, setStarredIds] = useState<ReadonlySet<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [reloadCount, setReloadCount] = useState(0);

  const reload = useCallback(() => setReloadCount((n) => n + 1), []);

  useEffect(() => {
    if (!orgId) return;
    const controller = new AbortController();
    const { signal } = controller;
    setLoading(true);
    setFailed(false);

    void (async () => {
      // Deliberately not org-scoped: a share can point into an organization
      // the recipient is not a member of, and must remain visible after it
      // has been opened.
      const sharedPromise = foldersApi.listNewSharedWithMe(null, SHARED_LIMIT, signal);
      const [starredResult, sharedResult, recentResult, starIdsResult] = await Promise.allSettled([
        // Starred shared files live in their owner's org, so they are let in by id.
        sharedPromise
          .catch(() => [])
          .then((shared) => foldersApi.listStarred(orgId, signal, shared.map(({ node }) => node.id))),
        sharedPromise,
        // Shared documents opened from Home belong in Last opened too, even
        // though they live in another organization.
        sharedPromise
          .catch(() => [])
          .then((shared) =>
            foldersApi.listRecentlyOpened(
              orgId,
              RECENT_LIMIT,
              undefined,
              signal,
              shared.map(({ node }) => node.id),
            ),
          ),
        foldersApi.listStarredIds(signal),
      ]);
      if (signal.aborted) return;

      const recentOpens = recentResult.status === 'fulfilled' ? recentResult.value : [];
      let frequentOpens: readonly FoldersNodeOpen[] = [];
      try {
        frequentOpens = await foldersApi.listFrequentlyOpened(
          orgId,
          FREQUENT_LIMIT,
          recentOpens.map((item) => item.node.id),
          signal,
        );
      } catch {
        frequentOpens = [];
      }
      if (signal.aborted) return;

      setStarred(starredResult.status === 'fulfilled' ? starredResult.value : []);
      setShared(sharedResult.status === 'fulfilled' ? sharedResult.value : []);
      setRecent(recentOpens);
      setFrequent(frequentOpens);
      setStarredIds(starIdsResult.status === 'fulfilled' ? starIdsResult.value : new Set());
      setFailed(
        [starredResult, sharedResult, recentResult].some((result) => result.status === 'rejected'),
      );
      setLoading(false);
    })();

    return () => controller.abort();
  }, [orgId, reloadCount]);

  const toggleStar = useCallback(
    (node: FoldersNode) => {
      if (!userId) return;
      const wasStarred = starredIds.has(node.id);

      // Optimistic on both the id set and the Starred list: a star that lags
      // behind the click reads as a broken control.
      setStarredIds((current) => {
        const next = new Set(current);
        if (wasStarred) next.delete(node.id);
        else next.add(node.id);
        return next;
      });
      setStarred((current) =>
        wasStarred ? current.filter((item) => item.id !== node.id) : [node, ...current],
      );

      void (wasStarred ? foldersApi.unstarNode(node.id, userId) : foldersApi.starNode(node.id, userId))
        .catch(() => { reload(); });
    },
    [reload, starredIds, userId],
  );

  return { starred, shared, recent, frequent, starredIds, loading, failed, reload, toggleStar };
}
